import { tap, map, concatMap } from 'rxjs/operators';
import { ApiResponse } from '@core/models/api-response.model';
import { Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Withdraw } from '@core/models/withdraw.model';
import { DefaultDataService, HttpUrlGenerator } from '@ngrx/data';
import { Pagination } from '@core/models/pagination.model';
import { Injectable } from '@angular/core';
import { WithdrawMultiBankTransaction } from '@core/models/withdraw-multi.model';
import { SKIP_ERROR_INTERCEPTOR_HEADER } from '@utils/constants';
import { WithdrawLimit } from '@core/models/withdraw-limit.model';
import { WithdrawLog } from '@core/models/withdraw-log.model';

@Injectable()
export class WithdrawDataService extends DefaultDataService<Withdraw>  {

  pagination: Pagination;
  messages$ = new Subject<any[]>();
  messages2$ = new Subject<any[]>();
  isSuccess: boolean;

  constructor(http: HttpClient, httpUrlGenerator: HttpUrlGenerator) {
    super('Withdraw', http, httpUrlGenerator, { root: '' });
  }

  getProcessingFeePreview(merchantBankId: number, amount: number): Observable<any> {
    return this.http
      .get<ApiResponse>(`/withdraw/processingfeepreview?merchant_bank_id=${merchantBankId}&amount=${amount}`)
      .pipe(map(res => res.data));
  }

  getAll(): Observable<Withdraw[]> {
    return this.http.get<ApiResponse>('/withdraw').pipe(
      tap(res => this.paginationInit(res)),
      map(res => res.data.rows)
    );
  }

  add(withdraw: Withdraw): Observable<Withdraw> {
    return this.http.post<ApiResponse>(`/withdraw`, withdraw).pipe(
      tap(res => this.messages$.next(res.message)),
      concatMap((entity) => this.http.get(`/withdraw/${entity.data.rows.id}`).pipe(
        map((row: ApiResponse) => row.data.rows)
      ))
    );
  }

  updateCurrentWithdraw(withdraw: any) {
    return this.http.put<ApiResponse>(`/withdraw/${withdraw.id}`, withdraw).pipe(
      map(res => res.data.rows)
    );
  }

  updateRemarks(withdraw: any) {
    return this.http.put<ApiResponse>(`/withdraw/remarks/${withdraw.id}`, withdraw).pipe(
      tap(res => this.messages$.next(res.message)),
      concatMap((entity) => this.http.get(`/withdraw/${entity.data.rows.id}`).pipe(
        map((row: ApiResponse) => row.data.rows)
      ))
    );
  }

  getById(id: number): Observable<Withdraw> {
    return this.http.get<ApiResponse>(`/withdraw/${id}`).pipe(
      map(res => res.data.rows)
    );
  }

  getWithQuery(pageParam: string): Observable<Withdraw[]> {
    return this.http.get<ApiResponse>(`/withdraw${pageParam}`).pipe(
      tap(res => this.paginationInit(res)),
      map(res => res.data.rows)
    )
  }

  createBankTransaction(data: WithdrawMultiBankTransaction) {
    return this.http.post<ApiResponse>(`/withdraw/banktransaction`, data).pipe(
      tap(res => {
        this.paginationInit(res);
        this.messages$.next(res.message)
      }),
      map(res => res.data.rows)
    );
  }

  exportWithdraw(pageParam: string) {
    return this.http.get<ApiResponse>(`/withdraw/export${pageParam}`).pipe(
      tap(res => this.messages$.next(res.message))
    );
  }

  createPayout(data: any, skipErrHandling: boolean = false) {
    const headers = {};

    if (skipErrHandling) {
      headers[SKIP_ERROR_INTERCEPTOR_HEADER] = 'true';
    }

    return this.http.post<ApiResponse>(`/withdraw/payout`, data, { headers }).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => res.data)
    )
  }

  createBankPayout(data: any) {
    return this.http.post<ApiResponse>(`/withdraw/bankpayout`, data).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => res.data)
    )
  }

  getPayoutStatus(transactionId: number) {
    return this.http.get<ApiResponse>(`/withdraw/payout/status?bank_transaction_id=${transactionId}`).pipe(
      tap(res => this.messages$.next(res.message)),
      map(res => res.data)
    )
  }

  retryBankPayout(bankTransactionId: number) {
    return this.http.put<ApiResponse>(`/withdraw/retrybankpayout/${bankTransactionId}`, null).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => res.data)
    )
  }

  // AIO-82573 — read banksys and bring the CMS row into line with it. Distinct from
  // retryBankPayout, which re-queues the withdrawal: syncing must never do that.
  syncBankPayout(bankTransactionId: number) {
    return this.http.put<ApiResponse>(`/withdraw/syncbankpayout/${bankTransactionId}`, null).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => res.data)
    )
  }

  // AIO-82573 — stop an auto-withdrawal the bank bot has not completed.
  rejectBankPayout(bankTransactionId: number) {
    return this.http.put<ApiResponse>(`/withdraw/rejectbankpayout/${bankTransactionId}`, null).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => res.data)
    )
  }

  // AIO-82573 — record that a transfer the bot did not complete was in fact paid.
  markBankPayoutSuccess(bankTransactionId: number, data: { reference_no: string, receipt_image: string }) {
    return this.http.put<ApiResponse>(`/withdraw/markbankpayoutsuccess/${bankTransactionId}`, data).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => res.data)
    )
  }

  updateBankTransaction(bankTransactionId: number, data) {
    return this.http.put<ApiResponse>(`/withdraw/banktransactions/${bankTransactionId}`, data).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => res.data)
    )
  }

  // AIO-79217: re-send the Telegram message for a Bank (Offline) payout the group never received.
  resendPayoutTelegram(bankTransactionId: number) {
    return this.http.post<ApiResponse>(`/withdraw/banktransactions/${bankTransactionId}/telegram/resend`, {}).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => res.data)
    )
  }

  getActiveSession(id: number) {
    return this.http.get<ApiResponse>(`/withdraw/activesession/${id}`).pipe(
      map(res => res.data)
    );
  }

  setActiveSession(data: any) {
    return this.http.post<ApiResponse>(`/withdraw/activesession`, data).pipe(
      map(res => res)
    );
  }

  triggerWithdrawCount() {
    return this.http.post<ApiResponse>(`/withdraw/counter`, []).pipe(
      tap(
        // res => this.messages$.next(res.message)
      )
    );
  }

  getCallbackStatus(transactionId: number) {
    return this.http.get<ApiResponse>(`/withdraw/callbackstatus/${transactionId}`).pipe(
      map(res => res.data)
    )
  }

  private paginationInit(res: any) {
    if (res) {
      this.pagination = res.data.paginations;
    }
  }

  getRemarksHistory(deposit_id: number) {
    return this.http.get<ApiResponse>(`/withdraw/remarks/${deposit_id}`).pipe(
      tap(
        // res => this.messages$.next(res.message)
      )
    );
  }

  getLimit(pageParam: string): Observable<WithdrawLimit> {
    return this.http.get<ApiResponse>(`/withdraw/limit${pageParam}`).pipe(
      map(res => res.data.rows)
    );
  }

  getWithdrawLog(pageParam: string): Observable<WithdrawLog[]> {
    return this.http.get<ApiResponse>(`/withdraw/log${pageParam}`).pipe(
      tap(res => {
        this.paginationInit(res);
      }),
      map(res => res.data.rows)
    );
  }
}
